import { Dictionaries } from 'models/Dictionaries';
import { DICTIONARY_PATHS } from 'constants/DictionaryConstants';
import { DictionaryName } from 'types/Dictionary';
import { MayBe } from 'types/common';
import { uniq } from 'utils/objectUtils';
import { CandidateProvider } from 'components/Keyboard/modules/candidates';

import { lookupExact, lookupExtensions } from '../Pinyin/provider';
import type { ChineseDictionary } from '../types';
import { CANGJIE_RADICAL_CODES } from './utils';

// The layout emits radical glyphs (e.g. 竹手戈); the dictionary is keyed by the
// Cangjie letter code (e.g. "hqi"), so the buffer is mapped back to letters here.
const toCode = (buffer: string): string =>
  buffer.split('').map((radical) => CANGJIE_RADICAL_CODES[radical] ?? '').join('');

const getDictionary = (): MayBe<ChineseDictionary> => {
  const dictionary = Dictionaries.get(DictionaryName.CANGJIE_TRADITIONAL) as MayBe<ChineseDictionary>;

  if (!dictionary) {
    console.warn(`[ChineseIME] Cangjie dict not loaded — deploy ${DICTIONARY_PATHS.CANGJIE_TRADITIONAL} to ASSETS_URL`);
  }
  return dictionary;
};

// Cangjie codes carry no embedded segmentation: a buffer is a single character's
// code, so exact + prefix-extension lookups are enough (no syllable splitting).
export const getCangjieCandidates: CandidateProvider = async (input) => {
  const dictionary = getDictionary();
  const code = toCode(input);
  if (!dictionary || !code) {
    return [];
  }

  return uniq([
    ...lookupExact(dictionary, code),
    ...lookupExtensions(dictionary, code),
  ]);
};

export const shouldCommitOnOverflow = async (
  buffer: string,
  radical: string,
  getCandidates: (glyphs: string) => Promise<string[]>,
): Promise<boolean> => {
  if (!buffer) {
    return false;
  }
  const candidates = await getCandidates(buffer + radical);
  return candidates.length === 0;
};